<template>
    <div>
        <form novalidate @submit.prevent>
            <div v-if="showRoles && !role" class="radio-list form__row">
                <label class="radio-label renter-radio-label">
                    <input v-model="$v.formData.role.$model" type="radio" class="radio" value="renter" />
                    {{ $t('modals.signup.role_label_renter') }}
                </label>
                <label class="radio-label">
                    <input v-model="$v.formData.role.$model" type="radio" class="radio" value="owner" />
                    {{ $t('modals.signup.role_label_owner') }}
                </label>
                <InputError v-if="!$v.formData.role.required && $v.formData.role.$dirty" static-position>
                    {{ $t('errors.field_required') }}
                </InputError>
            </div>
            <div class="form__row">
                <label class="label" for="email">
                    {{ $t('form_inputs.email_address_label') }}
                </label>
                <input
                    id="email"
                    v-model.trim="formData.email"
                    class="input"
                    type="email"
                    :class="{
                        'input--error': $v.formData.email.$invalid && $v.formData.email.$dirty,
                    }"
                    @blur="$v.formData.email.$touch()"
                    @focus="$v.formData.email.$reset()"
                />
                <template v-if="$v.formData.email.$dirty">
                    <InputError v-if="!$v.formData.email.required" static-position>
                        {{ $t('errors.field_required') }}
                    </InputError>
                    <InputError v-if="!$v.formData.email.email" static-position>
                        {{ $t('errors.field_invalid') }}
                    </InputError>
                </template>
            </div>
            <RecaptchaButton
                :disabled="isLoading"
                :loading="isLoading"
                :is-invalid="$v.formData.$invalid && $v.formData.email.$dirty"
                :label="$t('modals.signup.continue_with_mail')"
                :recaptcha-action="'intro_signup'"
                @start-verifying="onStartVerifying"
                @error="isLoading = false"
                @success="determineNextStepAfterEmailCheck"
            />
        </form>

        <template v-if="featureEnabled('ownerAccess') || featureEnabled('renterAccess')">
            <div class="sign-in__divider">
                {{ $t('modals.signup.divider_between_signup_options') }}
            </div>
            <SocialAuthButtons
                type="signup"
                :error-redirect-to="errorRedirectTo"
                @logged-in="() => $emit('logged-in')"
            />
        </template>
    </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, email } from 'vuelidate/lib/validators';
import { PASSWORD, FACEBOOK, GOOGLE } from '~/config/authMethods';
import modalMixin from '~/mixins/modalMixin';

import SocialAuthButtons from '~/components/onboarding/SocialAuthButtons';
import InputError from '~/components/form-elements/InputError.vue';
import RecaptchaButton from '~/components/form-elements/RecaptchaButton';

export default {
    components: {
        RecaptchaButton,
        SocialAuthButtons,
        InputError,
    },
    mixins: [validationMixin, modalMixin],
    props: {
        showRoles: {
            type: Boolean,
            default: true,
        },
        role: {
            type: String,
            default: null,
        },
        email: {
            type: String,
            default: null,
        },
        type: {
            type: String,
            default: 'modal',
        },
    },
    validations() {
        const validations = {
            formData: {
                role: { required },
                email: {
                    required,
                    email,
                },
            },
        };

        return validations;
    },
    data() {
        return {
            formData: {
                role: this.role,
                email: this.email,
            },
            isLoading: false,
        };
    },
    computed: {
        errorRedirectTo() {
            return this.type === 'modal'
                ? {
                      name: this.getRouteBaseName(),
                      query: { ...this.$route.query, popup: 'sign-up' },
                      params: this.$route.params,
                  }
                : {};
        },
    },
    methods: {
        onStartVerifying() {
            this.isLoading = true;
            this.$v.formData.$touch();
        },
        determineNextStepAfterEmailCheck(token) {
            const data = {
                email: this.formData.email,
                recaptchaToken: token,
            };

            this.isLoading = true;

            this.$authService
                .checkEmail(data)
                .then(response => {
                    if (response.exists) {
                        if (response.authMethod === PASSWORD) {
                            if (this.type === 'page') {
                                return this.$router.push(
                                    this.localePath({
                                        name: 'login',
                                        query: {
                                            email: this.formData.email,
                                        },
                                    })
                                );
                            }

                            this.displayLoginModal(null, { email: this.formData.email });
                        }

                        if ([FACEBOOK, GOOGLE].includes(response.authMethod)) {
                            if (this.type === 'page') {
                                return this.$router.push(
                                    this.localePath({
                                        name: 'login',
                                        query: {
                                            email: this.formData.email,
                                            authMethod: response.authMethod,
                                        },
                                    })
                                );
                            }

                            this.displayContinueLoginModal({
                                authMethod: response.authMethod,
                                email: this.formData.email,
                            });
                        }
                    } else {
                        this.$emit('email-not-found', this.formData);
                    }
                })
                .catch(async err => {
                    this.$sentry.captureException(err);
                    await this.$recaptcha.reset();
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/blocks/_sign-in.scss';
</style>
